import { useSEO } from '@/hooks/useSEO';
import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";
import { submitForm } from "@/lib/submitForm";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

const DESIGN_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "3D & 2D Engineering Design Services Kenya",
    "url": SITE_URL + "/design-services",
    "description": "AutoCAD 2D floor plans and 3D modelling services for commercial kitchens, HVAC, food processing and industrial facilities in Kenya. KEBS-compliant engineering drawings by ISMAN.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ISMAN Engineering Company",
      "url": SITE_URL,
      "telephone": "+254724114555"
    },
    "areaServed": { "@type": "Country", "name": "Kenya" },
    "serviceType": "Engineering Design & Drafting"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Design Services", "item": SITE_URL + "/design-services" }
    ]
  }
];

const offerings = [
  { icon: "ri-layout-3-line", image: "/images/design/floor-plan.webp", title: "2D Floor Plans", desc: "Precise floor plan layouts for kitchen, HVAC, plumbing, and MEP systems ready for contractor use and council submission." },
  { icon: "ri-box-3-line", image: "/images/design/3d-model.webp", title: "3D Modelling", desc: "Full AutoCAD 3D models with photorealistic renders — see your project before a single piece of steel is cut." },
  { icon: "ri-file-chart-line", image: "/images/design/equipment-schedule.webp", title: "Equipment Schedules", desc: "Detailed equipment lists, specifications, and utility load calculations to accompany every design package." },
  { icon: "ri-tools-line", image: "/images/design/shop-drawing.webp", title: "Shop Drawings", desc: "Fabrication-ready shop drawings with weld symbols, dimensions, and surface finish callouts for our workshop." },
  { icon: "ri-building-2-line", image: "/images/design/as-built.webp", title: "As-Built Drawings", desc: "Post-installation as-built drawings for your records, maintenance teams, and future expansion planning." },
  { icon: "ri-shield-check-line", image: "/images/design/staircase-detail.webp", title: "KEBS & Compliance", desc: "All drawings prepared to KEBS standards with structural calculations and compliance documentation included." },
];

const process = [
  { step: "01", title: "Brief & Site Survey", desc: "We visit your site, take measurements, and document all existing services — electrical, plumbing, gas, drainage." },
  { step: "02", title: "Concept Design", desc: "Initial 2D layout options are presented. We discuss workflow, equipment placement, and utility routing." },
  { step: "03", title: "3D Development", desc: "Approved concept is built into a full 3D model with photo renders for client sign-off." },
  { step: "04", title: "Final Drawing Package", desc: "Issued drawing set includes floor plans, elevations, sections, equipment schedule, and utility layouts." },
];

export default function DesignServicesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "Commercial Kitchen Design", details: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    try {
      await submitForm("design-services", data);
    } catch (_e) { /* ignore */ }
    setSubmitting(false);
    setSubmitted(true);
  };

  useSEO({
    title: '3D & 2D Engineering Design Services Kenya | AutoCAD Drawings | ISMAN Nairobi',
    description: 'Professional AutoCAD 2D and 3D engineering design services in Kenya — commercial kitchen, HVAC, food processing and industrial facility drawings. KEBS-compliant by ISMAN Engineering.',
    keywords: 'AutoCAD engineering design Kenya, 2D floor plan Kenya, 3D engineering design Nairobi, mechanical drawing Kenya, KEBS engineering drawings Kenya',
    canonical: SITE_URL + '/design-services',
    schema: DESIGN_SCHEMA,
  });

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />

      {/* Hero */}
      <section className="relative h-[480px] flex items-end overflow-hidden">
        <img src="/images/design/3d-model.webp" alt="Design Services" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 pb-14 w-full">
          <span className="inline-block bg-teal-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md mb-4">ISMAN Engineering</span>
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>3D &amp; 2D Engineering Design</h1>
          <p className="text-white/75 text-base max-w-2xl">Full AutoCAD design packages — from initial concept to fabrication-ready shop drawings and KEBS-compliant submissions.</p>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">What We Deliver</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>Complete Design Services</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((o) => (
              <div key={o.title} className="bg-white rounded-xl border border-gray-100 hover:border-teal-200 transition-all group overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden bg-white border-b border-gray-100">
                  <img src={o.image} alt={o.title} loading="lazy" className={`absolute inset-0 w-full h-full transition-transform duration-500 group-hover:scale-105 ${o.image.includes("3d-model") ? "object-cover" : "object-contain p-2"}`} />
                </div>
                <div className="p-6">
                <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-lg mb-4 group-hover:bg-teal-600 transition-colors">
                  <i className={`${o.icon} text-teal-600 group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{o.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{o.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Design Showcase</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { img: "/images/design/floor-plan.webp", label: "2D Kitchen Floor Plan" },
              { img: "/images/design/3d-model.webp", label: "3D Food Plant Model & Renders" },
              { img: "/images/design/as-built.webp", label: "HVAC & MEP As-Built Drawing" },
              { img: "/images/design/shop-drawing.webp", label: "Tank Shop Drawing" },
              { img: "/images/design/equipment-schedule.webp", label: "Equipment Schedule" },
              { img: "/images/design/staircase-detail.webp", label: "Steel Staircase Detail" },
            ].map((g) => (
              <a key={g.label} href={g.img} target="_blank" rel="noopener noreferrer" className="relative rounded-lg overflow-hidden group h-56 bg-white border border-gray-100 block">
                <img src={g.img} alt={g.label} loading="lazy" className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${g.img.includes("3d-model") ? "object-cover" : "object-contain p-2"}`} />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4 pt-10 flex items-end">
                  <span className="text-white text-sm font-semibold">{g.label}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-10 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Our Design Process</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p) => (
              <div key={p.step} className="text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-black text-lg">{p.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section id="inquiry" className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Request a Design Quote</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Tell us about your project and we&apos;ll get back to you within 1 business day.</p>
          {submitted ? (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
              <i className="ri-check-double-line text-4xl text-teal-600 mb-3 block"></i>
              <h3 className="font-bold text-gray-900 mb-1">Enquiry Received!</h3>
              <p className="text-gray-500 text-sm">Our design team will contact you within 1 business day.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-isman-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Full Name *</label>
                  <input required name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="Jane Kamau" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="jane@example.com" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Phone *</label>
                  <input required name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400" placeholder="0722 000 000" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Service Required *</label>
                  <select required name="service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer">
                    {["Commercial Kitchen Design", "HVAC Layout", "Food Processing Design", "Hospital/Pharmacy Design", "Railing Design", "Full Project Package"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Project Details</label>
                <textarea name="details" value={form.details} onChange={(e) => { if (e.target.value.length <= 500) setForm({ ...form, details: e.target.value }); }} rows={4} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 resize-none" placeholder="Describe your project, site dimensions, and requirements..."></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{form.details.length}/500</p>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-bold py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin"></i> Sending...</> : <><i className="ri-send-plane-line"></i> Send Enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
